import { parseLocaleNumber } from "./decimal";
import {
  convertInventoryCostPerUnitToBase,
  convertInventoryQuantityToBase,
  normalizeInventoryUnit,
} from "./unitConversion";

function toNumber(value) {
  const parsed = parseLocaleNumber(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatInventoryNumber(value) {
  const parsed = toNumber(value);
  if (parsed === null) return "";
  if (Math.abs(parsed - Math.round(parsed)) < 0.0001) {
    return String(Math.round(parsed));
  }
  return parsed.toFixed(4).replace(/\.?0+$/, "");
}

export function getPurchaseUnitLabel(item) {
  const purchaseUnitQuantity = convertInventoryQuantityToBase(item?.purchase_unit_quantity, item?.unit);
  const packageName = (item?.purchase_unit_name || "").trim();
  const unit = normalizeInventoryUnit(item?.unit);

  if (!purchaseUnitQuantity) {
    return packageName;
  }

  const baseLabel = `${formatInventoryNumber(purchaseUnitQuantity)} ${unit}`.trim();
  return packageName ? `${baseLabel} / ${packageName}` : baseLabel;
}

export function getDerivedCostPerUnit(item) {
  const purchaseUnitQuantity = convertInventoryQuantityToBase(item?.purchase_unit_quantity, item?.unit);
  const purchaseUnitCost = toNumber(item?.purchase_unit_cost);

  if (purchaseUnitQuantity && purchaseUnitCost !== null) {
    return purchaseUnitCost / purchaseUnitQuantity;
  }

  return convertInventoryCostPerUnitToBase(item?.cost_per_unit, item?.unit);
}

export function normalizeInventoryItemForm(item) {
  const unit = normalizeInventoryUnit(item?.unit || "kg");
  const currentQuantity = convertInventoryQuantityToBase(item?.current_quantity, item?.unit);
  const minimumQuantity = convertInventoryQuantityToBase(item?.minimum_quantity, item?.unit);
  const reorderPoint = convertInventoryQuantityToBase(item?.reorder_point, item?.unit);
  const purchaseUnitQuantity =
    item?.purchase_unit_quantity != null
      ? formatInventoryNumber(convertInventoryQuantityToBase(item.purchase_unit_quantity, item?.unit))
      : item?.cost_per_unit != null
        ? "1"
        : "";

  const purchaseUnitCost =
    item?.purchase_unit_cost != null
      ? formatInventoryNumber(item.purchase_unit_cost)
      : item?.cost_per_unit != null
        ? formatInventoryNumber(getDerivedCostPerUnit(item))
        : "";

  return {
    name: item?.name || "",
    article_number: item?.article_number || "",
    current_quantity: currentQuantity != null ? formatInventoryNumber(currentQuantity) : "",
    unit,
    purchase_unit_quantity: purchaseUnitQuantity,
    purchase_unit_name: item?.purchase_unit_name || "",
    minimum_quantity: minimumQuantity != null ? formatInventoryNumber(minimumQuantity) : "",
    reorder_point: reorderPoint != null ? formatInventoryNumber(reorderPoint) : "",
    supplier_id: item?.supplier_id != null ? String(item.supplier_id) : "",
    supplier: item?.supplier || "",
    lead_time_days: item?.lead_time_days != null ? String(item.lead_time_days) : "",
    category: item?.category || "Frische Zutaten",
    purchase_unit_cost: purchaseUnitCost,
  };
}

export function buildInventoryItemPayload(form) {
  const rawUnit = form.unit || "kg";
  const currentQuantity = toNumber(form.current_quantity);
  const minimumQuantity = toNumber(form.minimum_quantity);
  const reorderPoint = toNumber(form.reorder_point);
  const leadTimeDays = toNumber(form.lead_time_days);
  const purchaseUnitQuantity = toNumber(form.purchase_unit_quantity);
  const purchaseUnitCost = toNumber(form.purchase_unit_cost);
  const normalizedUnit = normalizeInventoryUnit(rawUnit);
  const canonicalCurrentQuantity = currentQuantity !== null
    ? convertInventoryQuantityToBase(currentQuantity, rawUnit)
    : undefined;
  const canonicalMinimumQuantity = minimumQuantity !== null
    ? convertInventoryQuantityToBase(minimumQuantity, rawUnit)
    : undefined;
  const canonicalReorderPoint = reorderPoint !== null
    ? convertInventoryQuantityToBase(reorderPoint, rawUnit)
    : undefined;
  const canonicalPurchaseUnitQuantity = purchaseUnitQuantity !== null
    ? convertInventoryQuantityToBase(purchaseUnitQuantity, rawUnit)
    : undefined;
  const supplierId =
    form.supplier_id === "" || form.supplier_id == null
      ? null
      : toNumber(form.supplier_id);
  const derivedCostPerUnit =
    canonicalPurchaseUnitQuantity && purchaseUnitCost !== null
      ? purchaseUnitCost / canonicalPurchaseUnitQuantity
      : purchaseUnitCost;

  return {
    ...form,
    article_number: form.article_number?.trim() || "",
    current_quantity: canonicalCurrentQuantity,
    unit: normalizedUnit,
    minimum_quantity: canonicalMinimumQuantity,
    reorder_point: canonicalReorderPoint,
    lead_time_days: leadTimeDays !== null ? Math.round(leadTimeDays) : undefined,
    purchase_unit_quantity: canonicalPurchaseUnitQuantity || undefined,
    purchase_unit_name: form.purchase_unit_name?.trim() || "",
    purchase_unit_cost: purchaseUnitCost !== null ? purchaseUnitCost : undefined,
    supplier_id: supplierId !== null ? supplierId : null,
    supplier: form.supplier?.trim() || "",
    cost_per_unit: derivedCostPerUnit !== null ? Number(derivedCostPerUnit.toFixed(2)) : undefined,
  };
}

export function getPurchasePlan(item, requiredQuantity) {
  const neededQuantity = Math.max(0, toNumber(requiredQuantity) || 0);
  const explicitPurchaseUnitQuantity = convertInventoryQuantityToBase(item?.purchase_unit_quantity, item?.unit);
  const purchaseUnitQuantity = explicitPurchaseUnitQuantity && explicitPurchaseUnitQuantity > 0
    ? explicitPurchaseUnitQuantity
    : null;
  const purchaseUnitCost = toNumber(item?.purchase_unit_cost);
  const derivedUnitCost = getDerivedCostPerUnit(item);
  const packageLabel = getPurchaseUnitLabel(item);
  const normalizedUnit = normalizeInventoryUnit(item?.unit);

  if (!purchaseUnitQuantity) {
    return {
      hasPurchaseConfig: false,
      coverageQuantity: neededQuantity,
      purchaseUnitsNeeded: null,
      purchaseUnitCost,
      estimatedCost: derivedUnitCost !== null ? neededQuantity * derivedUnitCost : 0,
      packageLabel,
      summary: null,
    };
  }

  const purchaseUnitsNeeded = neededQuantity > 0
    ? Math.max(1, Math.ceil(neededQuantity / purchaseUnitQuantity))
    : 1;
  const coverageQuantity = purchaseUnitsNeeded * purchaseUnitQuantity;
  const estimatedCost = purchaseUnitCost !== null
    ? purchaseUnitsNeeded * purchaseUnitCost
    : derivedUnitCost !== null
      ? coverageQuantity * derivedUnitCost
      : 0;

  return {
    hasPurchaseConfig: true,
    purchaseUnitQuantity,
    purchaseUnitCost,
    purchaseUnitsNeeded,
    coverageQuantity,
    estimatedCost,
    packageLabel,
    summary: `${formatInventoryNumber(purchaseUnitsNeeded)} x ${packageLabel || `${formatInventoryNumber(purchaseUnitQuantity)} ${normalizedUnit || ""}`.trim()}`.trim(),
  };
}

export function getOrderDraft(item, enteredAmount) {
  const enteredValue = Math.max(0, toNumber(enteredAmount) || 0);
  const purchasePlan = getPurchasePlan(item, enteredValue);
  const normalizedUnit = normalizeInventoryUnit(item?.unit);

  if (!purchasePlan.hasPurchaseConfig) {
    return {
      usesPurchaseUnits: false,
      enteredAmount: enteredValue,
      purchaseUnitsOrdered: null,
      coverageQuantity: enteredValue,
      estimatedCost: purchasePlan.estimatedCost,
      packageLabel: purchasePlan.packageLabel,
      summary: enteredValue > 0 ? `${formatInventoryNumber(enteredValue)} ${normalizedUnit || ""}`.trim() : "",
    };
  }

  const purchaseUnitsOrdered = enteredValue;
  const coverageQuantity = purchaseUnitsOrdered * purchasePlan.purchaseUnitQuantity;
  const estimatedCost = purchasePlan.purchaseUnitCost != null
    ? purchaseUnitsOrdered * purchasePlan.purchaseUnitCost
    : purchasePlan.estimatedCost;

  return {
    usesPurchaseUnits: true,
    enteredAmount: purchaseUnitsOrdered,
    purchaseUnitsOrdered,
    coverageQuantity,
    estimatedCost,
    packageLabel: purchasePlan.packageLabel,
    summary: `${formatInventoryNumber(purchaseUnitsOrdered)} x ${purchasePlan.packageLabel || ""}`.trim(),
  };
}
